if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();

        this.dispatchEvent(
          new CustomEvent('product-info:loaded', {
            bubbles: true
          })
        );

        // Filter Extra Mattress options on initial page load
        requestAnimationFrame(() => {
          this.filterSecondVariantOptions();
        });
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');

        if (!this.quantityForm) return;

        this.setQuantityBoundries();

        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(
            PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html
            .querySelectorAll('.scroll-trigger')
            .forEach((element) =>
              element.classList.add('scroll-trigger--cancel')
            )
        );

        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({
        data: { event, target, selectedOptionValues }
      }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl =
          target.dataset.productUrl ||
          this.pendingRequestUrl ||
          this.dataset.url;

        this.pendingRequestUrl = productUrl;

        const shouldSwapProduct =
          this.dataset.url !== productUrl;

        const shouldFetchFullPage =
          this.dataset.updateUrl === 'true' &&
          shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(
            productUrl,
            selectedOptionValues,
            shouldFetchFullPage
          ),

          targetId: target.id,

          callback: shouldSwapProduct
            ? this.handleSwapProduct(
                productUrl,
                shouldFetchFullPage
              )
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;

        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage
            ? "product-info[id^='MainProduct']"
            : 'product-info';

          const variant = this.getSelectedVariant(
            html.querySelector(selector)
          );

          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML =
              html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({
        requestUrl,
        targetId,
        callback
      }) {
        this.abortController?.abort();

        this.abortController = new AbortController();

        fetch(requestUrl, {
          signal: this.abortController.signal
        })
          .then((response) => response.text())

          .then((responseText) => {
            this.pendingRequestUrl = null;

            const html = new DOMParser().parseFromString(
              responseText,
              'text/html'
            );

            callback(html);
          })

          .then(() => {
            // Set focus to last clicked option value
            document
              .querySelector(`#${targetId}`)
              ?.focus();
          })

          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant =
          productInfoNode.querySelector(
            'variant-selects [data-selected-variant]'
          )?.innerHTML;

        return !!selectedVariant
          ? JSON.parse(selectedVariant)
          : null;
      }

      buildRequestUrlWithParams(
        url,
        optionValues,
        shouldFetchFullPage = false
      ) {
        const params = [];

        !shouldFetchFullPage &&
          params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(
            `option_values=${optionValues.join(',')}`
          );
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects =
          html.querySelector('variant-selects');

        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(
            this.variantSelectors,
            variantSelects,
            this.preProcessHtmlCallbacks
          );
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);

          this.updateOptionValues(html);

          // Filter Extra Mattress options after Shopify
          // re-renders the variant selectors.
          requestAnimationFrame(() => {
            this.filterSecondVariantOptions();
          });

          this.updateURL(
            productUrl,
            variant?.id
          );

          this.updateVariantInputs(
            variant?.id
          );

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(
            html,
            variant?.featured_media?.id
          );

          const updateSourceFromDestination = (
            id,
            shouldHide = (source) => false
          ) => {
            const source =
              html.getElementById(
                `${id}-${this.sectionId}`
              );

            if (!source) return;

            const destinations =
              this.querySelectorAll(
                `#${id}-${this.dataset.section},
                 #${id}-mobile-${this.dataset.section}`
              );

            destinations.forEach(
              (destination) => {
                destination.innerHTML =
                  source.innerHTML;

                destination.classList.toggle(
                  'hidden',
                  shouldHide(source)
                );
              }
            );
          };

          updateSourceFromDestination('price');

          updateSourceFromDestination(
            'Sku',
            ({ classList }) =>
              classList.contains('hidden')
          );

          updateSourceFromDestination(
            'Inventory',
            ({ innerText }) =>
              innerText === ''
          );

          updateSourceFromDestination('Volume');

          updateSourceFromDestination(
            'Price-Per-Item',
            ({ classList }) =>
              classList.contains('hidden')
          );

          this.updateQuantityRules(
            this.sectionId,
            html
          );

          this.querySelector(
            `#Quantity-Rules-${this.dataset.section}`
          )?.classList.remove('hidden');

          this.querySelector(
            `#Volume-Note-${this.dataset.section}`
          )?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html
              .getElementById(
                `ProductSubmitButton-${this.sectionId}`
              )
              ?.hasAttribute('disabled') ?? true,

            window.variantStrings.soldOut
          );

          publish(
            PUB_SUB_EVENTS.variantChange,
            {
              data: {
                sectionId: this.sectionId,
                html,
                variant,
              },
            }
          );
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section},
           #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input =
            productForm.querySelector(
              'input[name="id"]'
            );

          if (!input) return;

          input.value =
            variantId ?? '';

          input.dispatchEvent(
            new Event('change', {
              bubbles: true
            })
          );
        });
      }

      updateURL(url, variantId) {
        this.querySelector(
          'share-button'
        )?.updateUrl(
          `${window.shopUrl}${url}${
            variantId
              ? `?variant=${variantId}`
              : ''
          }`
        );

        if (
          this.dataset.updateUrl === 'false'
        ) {
          return;
        }

        window.history.replaceState(
          {},
          '',
          `${url}${
            variantId
              ? `?variant=${variantId}`
              : ''
          }`
        );
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(
          true,
          window.variantStrings.unavailable
        );

        const selectors = [
          'price',
          'Inventory',
          'Sku',
          'Price-Per-Item',
          'Volume-Note',
          'Volume',
          'Quantity-Rules'
        ]
          .map(
            (id) =>
              `#${id}-${this.dataset.section}`
          )
          .join(', ');

        document
          .querySelectorAll(selectors)
          .forEach(
            ({ classList }) =>
              classList.add('hidden')
          );
      }

      updateMedia(
        html,
        variantFeaturedMediaId
      ) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource =
          this.querySelector(
            'media-gallery ul'
          );

        const mediaGalleryDestination =
          html.querySelector(
            'media-gallery ul'
          );

        const refreshSourceData = () => {
          if (
            this.hasAttribute(
              'data-zoom-on-hover'
            )
          ) {
            enableZoomOnHover(2);
          }

          const mediaGallerySourceItems =
            Array.from(
              mediaGallerySource.querySelectorAll(
                'li[data-media-id]'
              )
            );

          const sourceSet =
            new Set(
              mediaGallerySourceItems.map(
                (item) =>
                  item.dataset.mediaId
              )
            );

          const sourceMap =
            new Map(
              mediaGallerySourceItems.map(
                (item, index) => [
                  item.dataset.mediaId,
                  {
                    item,
                    index
                  }
                ]
              )
            );

          return [
            mediaGallerySourceItems,
            sourceSet,
            sourceMap
          ];
        };

        if (
          mediaGallerySource &&
          mediaGalleryDestination
        ) {
          let [
            mediaGallerySourceItems,
            sourceSet,
            sourceMap
          ] = refreshSourceData();

          const mediaGalleryDestinationItems =
            Array.from(
              mediaGalleryDestination.querySelectorAll(
                'li[data-media-id]'
              )
            );

          const destinationSet =
            new Set(
              mediaGalleryDestinationItems.map(
                ({ dataset }) =>
                  dataset.mediaId
              )
            );

          let shouldRefresh = false;

          // Add items from new data
          // not present in DOM
          for (
            let i =
              mediaGalleryDestinationItems.length - 1;
            i >= 0;
            i--
          ) {
            if (
              !sourceSet.has(
                mediaGalleryDestinationItems[i]
                  .dataset.mediaId
              )
            ) {
              mediaGallerySource.prepend(
                mediaGalleryDestinationItems[i]
              );

              shouldRefresh = true;
            }
          }

          // Remove items from DOM
          // not present in new data
          for (
            let i = 0;
            i <
            mediaGallerySourceItems.length;
            i++
          ) {
            if (
              !destinationSet.has(
                mediaGallerySourceItems[i]
                  .dataset.mediaId
              )
            ) {
              mediaGallerySourceItems[i].remove();

              shouldRefresh = true;
            }
          }

          // Refresh
          if (shouldRefresh) {
            [
              mediaGallerySourceItems,
              sourceSet,
              sourceMap
            ] = refreshSourceData();
          }

          // Match destination order
          mediaGalleryDestinationItems.forEach(
            (
              destinationItem,
              destinationIndex
            ) => {
              const sourceData =
                sourceMap.get(
                  destinationItem.dataset.mediaId
                );

              if (
                sourceData &&
                sourceData.index !==
                  destinationIndex
              ) {
                mediaGallerySource.insertBefore(
                  sourceData.item,
                  mediaGallerySource.querySelector(
                    `li:nth-of-type(${
                      destinationIndex + 1
                    })`
                  )
                );

                [
                  mediaGallerySourceItems,
                  sourceSet,
                  sourceMap
                ] = refreshSourceData();
              }
            }
          );
        }

        // Set featured media
        // as active
        this.querySelector(
          'media-gallery'
        )?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // Update media modal
        const modalContent =
          this.productModal?.querySelector(
            '.product-media-modal__content'
          );

        const newModalContent =
          html.querySelector(
            'product-modal .product-media-modal__content'
          );

        if (
          modalContent &&
          newModalContent
        ) {
          modalContent.innerHTML =
            newModalContent.innerHTML;
        }
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity:
            this.quantityInput.dataset.cartQuantity
              ? parseInt(
                  this.quantityInput.dataset.cartQuantity
                )
              : 0,

          min:
            this.quantityInput.dataset.min
              ? parseInt(
                  this.quantityInput.dataset.min
                )
              : 1,

          max:
            this.quantityInput.dataset.max
              ? parseInt(
                  this.quantityInput.dataset.max
                )
              : null,

          step:
            this.quantityInput.step
              ? parseInt(
                  this.quantityInput.step
                )
              : 1,
        };

        let min = data.min;

        const max =
          data.max === null
            ? data.max
            : data.max -
              data.cartQuantity;

        if (max !== null) {
          min = Math.min(
            min,
            max
          );
        }

        if (
          data.cartQuantity >=
          data.min
        ) {
          min = Math.min(
            min,
            data.step
          );
        }

        this.quantityInput.min =
          min;

        if (max) {
          this.quantityInput.max =
            max;
        } else {
          this.quantityInput.removeAttribute(
            'max'
          );
        }

        this.quantityInput.value =
          min;

        publish(
          PUB_SUB_EVENTS.quantityUpdate,
          undefined
        );
      }

      fetchQuantityRules() {
        const currentVariantId =
          this.productForm?.variantIdInput
            ?.value;

        if (!currentVariantId) return;

        this.querySelector(
          '.quantity__rules-cart .loading__spinner'
        )?.classList.remove(
          'hidden'
        );

        return fetch(
          `${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`
        )
          .then(
            (response) =>
              response.text()
          )

          .then((responseText) => {
            const html =
              new DOMParser().parseFromString(
                responseText,
                'text/html'
              );

            this.updateQuantityRules(
              this.dataset.section,
              html
            );
          })

          .catch((e) =>
            console.error(e)
          )

          .finally(() =>
            this.querySelector(
              '.quantity__rules-cart .loading__spinner'
            )?.classList.add(
              'hidden'
            )
          );
      }

      updateQuantityRules(
        sectionId,
        html
      ) {
        if (!this.quantityInput) return;

        this.setQuantityBoundries();

        const quantityFormUpdated =
          html.getElementById(
            `Quantity-Form-${sectionId}`
          );

        if (!quantityFormUpdated) return;

        const selectors = [
          '.quantity__input',
          '.quantity__rules',
          '.quantity__label'
        ];

        for (
          let selector of selectors
        ) {
          const current =
            this.quantityForm.querySelector(
              selector
            );

          const updated =
            quantityFormUpdated.querySelector(
              selector
            );

          if (
            !current ||
            !updated
          ) {
            continue;
          }

          if (
            selector ===
            '.quantity__input'
          ) {
            const attributes = [
              'data-cart-quantity',
              'data-min',
              'data-max',
              'step'
            ];

            for (
              let attribute of attributes
            ) {
              const valueUpdated =
                updated.getAttribute(
                  attribute
                );

              if (
                valueUpdated !== null
              ) {
                current.setAttribute(
                  attribute,
                  valueUpdated
                );
              } else {
                current.removeAttribute(
                  attribute
                );
              }
            }
          } else {
            current.innerHTML =
              updated.innerHTML;

            if (
              selector ===
              '.quantity__label'
            ) {
              const updatedAriaLabelledBy =
                updated.getAttribute(
                  'aria-labelledby'
                );

              if (
                updatedAriaLabelledBy
              ) {
                current.setAttribute(
                  'aria-labelledby',
                  updatedAriaLabelledBy
                );

                // Update referenced
                // visually hidden element
                const labelId =
                  updatedAriaLabelledBy;

                const currentHiddenLabel =
                  document.getElementById(
                    labelId
                  );

                const updatedHiddenLabel =
                  html.getElementById(
                    labelId
                  );

                if (
                  currentHiddenLabel &&
                  updatedHiddenLabel
                ) {
                  currentHiddenLabel.textContent =
                    updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      /*
       * ============================================================
       * EXTRA MATTRESS FILTER
       * ============================================================
       *
       * This function ONLY works when an Extra Mattress option exists.
       *
       * It does NOT affect:
       * - Table extension
       * - Other second options
       * - Other products without mattress options
       *
       * Mattress fieldset is detected using:
       * - input name containing "תוספת מזרן"
       * - "Extra mattress"
       * - legend containing "מזרן"
       * - legend containing "Extra mattress"
       */

      filterSecondVariantOptions() {
        const variantSelects =
          this.querySelector(
            'variant-selects'
          );

        if (!variantSelects) return;

        const fieldsets = Array.from(
          variantSelects.querySelectorAll(
            'fieldset'
          )
        );

        if (
          fieldsets.length < 2
        ) {
          return;
        }

        /*
         * Find ONLY the Extra Mattress fieldset.
         */
        const mattressFieldset =
          fieldsets.find(
            (fieldset) => {
              const input =
                fieldset.querySelector(
                  'input'
                );

              if (!input) {
                return false;
              }

              const inputName =
                (
                  input.getAttribute(
                    'name'
                  ) || ''
                ).trim();

              const legend =
                (
                  fieldset.querySelector(
                    'legend'
                  )?.textContent ||
                  ''
                ).trim();

              return (
                inputName.includes(
                  'תוספת מזרן'
                ) ||

                inputName
                  .toLowerCase()
                  .includes(
                    'extra mattress'
                  ) ||

                legend.includes(
                  'מזרן'
                ) ||

                legend
                  .toLowerCase()
                  .includes(
                    'extra mattress'
                  )
              );
            }
          );

        /*
         * IMPORTANT:
         * If there is no Extra Mattress fieldset,
         * do absolutely nothing.
         *
         * This is what prevents Table Extension
         * from being affected.
         */
        if (
          !mattressFieldset
        ) {
          return;
        }

        const mattressIndex =
          fieldsets.indexOf(
            mattressFieldset
          );

        if (
          mattressIndex === 0
        ) {
          return;
        }

        /*
         * The bed-size fieldset is the fieldset
         * immediately before Extra Mattress.
         */
        const bedSizeFieldset =
          fieldsets[
            mattressIndex - 1
          ];

        if (
          !bedSizeFieldset
        ) {
          return;
        }

        /*
         * Get currently selected bed size.
         */
        const selectedSizeInput =
          bedSizeFieldset.querySelector(
            'input[type="radio"]:checked'
          );

        if (
          !selectedSizeInput
        ) {
          return;
        }

        const selectedSize =
          (
            selectedSizeInput.value ||
            ''
          ).trim();

        if (!selectedSize) {
          return;
        }

        /*
         * These options should ALWAYS remain visible.
         */
        const noMattressValues = [
          'בלי מזרן',
          'ללא מזרן'
        ];

        const mattressInputs =
          mattressFieldset.querySelectorAll(
            'input[type="radio"]'
          );

        let noMattressInput =
          null;

        /*
         * Find the "Without Mattress" option.
         */
        mattressInputs.forEach(
          (input) => {
            const value =
              (
                input.value ||
                ''
              ).trim();

            if (
              noMattressValues.includes(
                value
              )
            ) {
              noMattressInput =
                input;
            }
          }
        );

        /*
         * Filter mattress options.
         */
        mattressInputs.forEach(
          (input) => {
            const label =
              mattressFieldset.querySelector(
                `label[for="${input.id}"]`
              );

            if (!label) {
              return;
            }

            const mattressValue =
              (
                input.value ||
                ''
              ).trim();

            /*
             * Always show:
             * בלי מזרן
             * ללא מזרן
             */
            if (
              noMattressValues.includes(
                mattressValue
              )
            ) {
              input.hidden = false;

              label.style.display =
                '';

              label.removeAttribute(
                'aria-hidden'
              );

              return;
            }

            /*
             * Check whether mattress size
             * matches selected bed size.
             *
             * Example:
             *
             * selectedSize = 160/190
             *
             * mattressValue =
             * מזרן אדיוקיישן 160/190
             *
             * => visible
             */
            const isRelevant =
              mattressValue.includes(
                selectedSize
              );

            if (
              isRelevant
            ) {
              input.hidden = false;

              label.style.display =
                '';

              label.removeAttribute(
                'aria-hidden'
              );
            } else {
              /*
               * Completely hide irrelevant
               * mattress option.
               */
              input.hidden = true;

              label.style.display =
                'none';

              label.setAttribute(
                'aria-hidden',
                'true'
              );

              /*
               * If the hidden mattress was
               * previously selected, switch
               * automatically to Without Mattress.
               */
              if (
                input.checked &&
                noMattressInput
              ) {
                noMattressInput.checked =
                  true;

                noMattressInput.dispatchEvent(
                  new Event(
                    'change',
                    {
                      bubbles: true
                    }
                  )
                );
              }
            }
          }
        );
      }

      get productForm() {
        return this.querySelector(
          `product-form`
        );
      }

      get productModal() {
        return document.querySelector(
          `#ProductModal-${this.dataset.section}`
        );
      }

      get pickupAvailability() {
        return this.querySelector(
          `pickup-availability`
        );
      }

      get variantSelectors() {
        return this.querySelector(
          'variant-selects'
        );
      }

      get relatedProducts() {
        const relatedProductsSectionId =
          SectionId.getIdForSection(
            SectionId.parseId(
              this.sectionId
            ),
            'related-products'
          );

        return document.querySelector(
          `product-recommendations[data-section-id^="${relatedProductsSectionId}"]`
        );
      }

      get quickOrderList() {
        const quickOrderListSectionId =
          SectionId.getIdForSection(
            SectionId.parseId(
              this.sectionId
            ),
            'quick_order_list'
          );

        return document.querySelector(
          `quick-order-list[data-id^="${quickOrderListSectionId}"]`
        );
      }

      get sectionId() {
        return (
          this.dataset.originalSection ||
          this.dataset.section
        );
      }
    }
  );
}


/*
 * ============================================================
 * CUSTOM PRICE UPDATE
 * ============================================================
 */

document.addEventListener(
  'change',
  function (e) {
    const variantInput =
      e.target.closest(
        'input[name="id"]'
      );

    if (!variantInput) {
      return;
    }

    const productInfo =
      document.querySelector(
        'product-info'
      );

    if (!productInfo) {
      return;
    }

    const variantId =
      variantInput.value;

    if (!variantId) {
      return;
    }

    fetch(
      window.location.pathname +
        '?variant=' +
        variantId +
        '&section_id=' +
        productInfo.dataset.section
    )
      .then(
        (response) =>
          response.text()
      )

      .then((html) => {
        const parser =
          new DOMParser();

        const doc =
          parser.parseFromString(
            html,
            'text/html'
          );

        const newPriceBox =
          doc.querySelector(
            '.pricemid'
          );

        const currentPriceBox =
          document.querySelector(
            '.pricemid'
          );

        if (
          newPriceBox &&
          currentPriceBox
        ) {
          currentPriceBox.innerHTML =
            newPriceBox.innerHTML;
        }
      })

      .catch((error) => {
        console.error(
          'Price update error:',
          error
        );
      });
  }
);


/*
 * ============================================================
 * REMEMBER LAST SELECTED RADIO
 * ============================================================
 */

let lastSelectedRadio = null;

document.addEventListener(
  'mousedown',
  function (e) {
    const label =
      e.target.closest(
        'variant-selects label'
      );

    if (!label) {
      return;
    }

    const radio =
      document.getElementById(
        label.getAttribute('for')
      );

    if (!radio) {
      return;
    }

    if (radio.checked) {
      lastSelectedRadio =
        radio;
    } else {
      lastSelectedRadio =
        null;
    }
  }
);


/*
 * ============================================================
 * CLICK SELECTED MATTRESS AGAIN
 * ============================================================
 *
 * This behavior is ONLY for Extra Mattress.
 *
 * It will NOT affect:
 * - Table Extension
 * - Size
 * - Other product options
 */

document.addEventListener(
  'click',
  function (e) {
    if (
      !lastSelectedRadio
    ) {
      return;
    }

    const label =
      e.target.closest(
        'variant-selects label'
      );

    if (!label) {
      return;
    }

    const radio =
      document.getElementById(
        label.getAttribute('for')
      );

    if (!radio) {
      return;
    }

    if (
      radio !==
      lastSelectedRadio
    ) {
      lastSelectedRadio =
        null;

      return;
    }

    /*
     * Only handle this behavior
     * inside the Extra Mattress fieldset.
     */
    const fieldset =
      radio.closest(
        'fieldset'
      );

    if (!fieldset) {
      lastSelectedRadio =
        null;

      return;
    }

    const inputName =
      (
        radio.getAttribute(
          'name'
        ) || ''
      ).toLowerCase();

    const legend =
      (
        fieldset.querySelector(
          'legend'
        )?.textContent ||
        ''
      ).trim()
      .toLowerCase();

    const isMattressFieldset =
      inputName.includes(
        'תוספת מזרן'
      ) ||
      inputName.includes(
        'extra mattress'
      ) ||
      legend.includes(
        'מזרן'
      ) ||
      legend.includes(
        'extra mattress'
      );

    /*
     * If this is NOT Extra Mattress,
     * leave it completely alone.
     */
    if (
      !isMattressFieldset
    ) {
      lastSelectedRadio =
        null;

      return;
    }

    e.preventDefault();

    /*
     * Find "Without Mattress".
     */
    const defaultOption =
      fieldset.querySelector(
        'input[value="ללא מזרן"]'
      ) ||
      fieldset.querySelector(
        'input[value="בלי מזרן"]'
      );

    if (
      defaultOption &&
      defaultOption !== radio
    ) {
      defaultOption.checked =
        true;

      defaultOption.dispatchEvent(
        new Event(
          'change',
          {
            bubbles: true
          }
        )
      );
    }

    lastSelectedRadio =
      null;
  }
);